
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";

const EVENT_OPTIONS = [
  { value: "", label: "Todos los eventos" },
  { value: "LOGIN_SUCCESS", label: "Login exitoso" },
  { value: "LOGIN_FAILED", label: "Login fallido" },
  { value: "USER_CREATED", label: "Usuario creado" },
  { value: "USER_UPDATED", label: "Usuario actualizado" },
  { value: "USER_DEACTIVATED", label: "Usuario desactivado" },
  { value: "REPORT_GENERATED", label: "Reporte generado" },
  { value: "SENSOR_CLAIMED", label: "Sensor vinculado" },
  { value: "SENSOR_UNCLAIMED", label: "Sensor desvinculado" },
  { value: "AUDIT_TRAIL_EXPORTED", label: "Audit trail exportado" },
];

const EVENT_LABELS = {
  LOGIN_SUCCESS: "Login exitoso",
  LOGIN_FAILED: "Login fallido",
  USER_CREATED: "Usuario creado",
  USER_UPDATED: "Usuario actualizado",
  USER_DEACTIVATED: "Usuario desactivado",
  REPORT_GENERATED: "Reporte generado",
  SENSOR_CLAIMED: "Sensor vinculado",
  SENSOR_UNCLAIMED: "Sensor desvinculado",
  AUDIT_TRAIL_EXPORTED: "Audit trail exportado",
};

const EVENT_COLORS = {
  LOGIN_SUCCESS: { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  LOGIN_FAILED: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
  USER_CREATED: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  USER_UPDATED: { bg: "#f8fafc", color: "#334155", border: "#cbd5e1" },
  USER_DEACTIVATED: { bg: "#fff7ed", color: "#c2410c", border: "#fdba74" },
  REPORT_GENERATED: { bg: "#f5f3ff", color: "#6d28d9", border: "#c4b5fd" },
  SENSOR_CLAIMED: { bg: "#ecfeff", color: "#0f766e", border: "#99f6e4" },
  SENSOR_UNCLAIMED: { bg: "#fff7ed", color: "#c2410c", border: "#fdba74" },
  AUDIT_TRAIL_EXPORTED: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
};

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("es-EC", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function prettyJson(obj) {
  if (!obj) return "—";
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}

function getReason(row) {
  return row?.new_values?.reason || "";
}

function buildSummary(row) {
  const event = row?.event_type;
  const data = row?.new_values || {};
  const entityId = row?.entity_id || "—";
  const reason = getReason(row);

  switch (event) {
    case "LOGIN_SUCCESS":
      return "Acceso correcto al sistema.";
    case "LOGIN_FAILED":
      return data?.reason === "INVALID_PASSWORD"
        ? "Intento fallido por contraseña incorrecta."
        : "Intento fallido de acceso.";
    case "USER_CREATED":
      return `Se creó el usuario ${data?.username || entityId}.`;
    case "USER_UPDATED":
      return reason
        ? `Se actualizaron datos de ${data?.username || entityId}. Motivo: ${reason}`
        : `Se actualizaron datos de ${data?.username || entityId}.`;
    case "USER_DEACTIVATED":
      return reason
        ? `Se desactivó el usuario ${row?.old_values?.username || entityId}. Motivo: ${reason}`
        : `Se desactivó el usuario ${row?.old_values?.username || entityId}.`;
    case "REPORT_GENERATED":
      return `Reporte ${data?.format?.toUpperCase?.() || ""} generado para ${data?.type || "sensor"}.`.trim();
    case "SENSOR_CLAIMED":
      return `Se vinculó el sensor ${entityId}.`;
    case "SENSOR_UNCLAIMED":
      return `Se desvinculó el sensor ${entityId}.`;
    case "AUDIT_TRAIL_EXPORTED":
      return "Se exportó una copia del audit trail para revisión.";
    default:
      return "Evento registrado en el sistema.";
  }
}

export default function AuditTrailPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const token = localStorage.getItem("token");
  const canManageUsers = !!user?.permissions?.includes("manage_users");
  const part11Enabled = !!user?.part11_enabled;
  const canAccessPart11 = canManageUsers && part11Enabled;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageMsg, setPageMsg] = useState("");
  const [pageMsgType, setPageMsgType] = useState("info");

  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  const loadAuditTrail = async () => {
    setLoading(true);
    setPageMsg("");

    try {
      const qs = new URLSearchParams();
      if (eventType) qs.set("event_type", eventType);
      qs.set("limit", "200");

      const url = `/api/sensores/admin/audit-trail${qs.toString() ? `?${qs.toString()}` : ""}`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "No se pudo cargar el audit trail");
      }

      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setPageMsg(err.message || "Error cargando audit trail");
      setPageMsgType("error");
    } finally {
      setLoading(false);
    }
  };

const handleExportExcel = async () => {
  try {
    const qs = new URLSearchParams();
    if (eventType) qs.set("event_type", eventType);
    if (search) qs.set("search", search);

    const url = `/api/sensores/admin/audit-trail/export${qs.toString() ? `?${qs.toString()}` : ""}`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.error || "No se pudo exportar el audit trail");
    }

    const blob = await res.blob();
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = "audit_trail.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
  } catch (err) {
    setPageMsg(err.message || "Error exportando audit trail");
    setPageMsgType("error");
  }
};


useEffect(() => {
  if (!canAccessPart11) return;
  loadAuditTrail();
}, [eventType, canAccessPart11]); // eslint-disable-line

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const haystack = [
        r.username,
        r.event_type,
        buildSummary(r),
        JSON.stringify(r.old_values || {}),
        JSON.stringify(r.new_values || {}),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [rows, search]);

  if (!user) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={styles.text}>Cargando usuario...</p>
        </div>
      </div>
    );
  }


if (!canAccessPart11) {
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.title}>Acceso no autorizado</h2>
        <p style={styles.text}>
          Este módulo solo está disponible para equipos con CFR 21 Part 11 habilitado.
        </p>
        <button style={styles.primaryBtn} onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>
    </div>
  );
}

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.mainTitle}>Audit Trail</h1>
            <p style={styles.subtitle}>
              Equipo: <strong>{user?.team || "Sin equipo"}</strong>
            </p>
          </div>

        <div style={styles.headerActions}>
        <button style={styles.primaryBtn} onClick={handleExportExcel}>
        Exportar Excel
        </button>
        <button style={styles.secondaryBtn} onClick={loadAuditTrail}>
        Recargar
        </button>
        <button style={styles.secondaryBtn} onClick={() => navigate(-1)}>
        Volver
        </button>
        </div>
        </div>

        <div style={styles.filtersRow}>
          <input
            type="text"
            placeholder="Buscar por usuario, evento o detalle..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />

         <div style={styles.auditNote}>
          La exportación es una copia para revisión. El registro oficial permanece en el sistema.
          </div>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            style={styles.select}
          >
            {EVENT_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <div style={styles.counterBadge}>
            {filteredRows.length} registro{filteredRows.length === 1 ? "" : "s"}
          </div>
        </div>

        {pageMsg ? (
          <div
            style={{
              ...styles.message,
              ...(pageMsgType === "error" ? styles.messageError : styles.messageInfo),
            }}
          >
            {pageMsg}
          </div>
        ) : null}

        {loading ? (
          <div style={styles.card}>
            <p style={styles.text}>Cargando audit trail...</p>
          </div>
        ) : filteredRows.length === 0 ? (
          <div style={styles.card}>
            <p style={styles.text}>No hay registros para mostrar.</p>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Fecha y hora</th>
                  <th style={styles.th}>Usuario</th>
                  <th style={styles.th}>Evento</th>
                  <th style={styles.th}>Resumen</th>
                  <th style={styles.th}>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const color = EVENT_COLORS[row.event_type] || EVENT_COLORS.USER_UPDATED;

                  return (
                    <tr key={row.id}>
                      <td style={styles.tdDate}>{formatDateTime(row.performed_at)}</td>
                      <td style={styles.tdUser}>{row.username || "—"}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.badge,
                            background: color.bg,
                            color: color.color,
                            borderColor: color.border,
                          }}
                        >
                          {EVENT_LABELS[row.event_type] || row.event_type}
                        </span>
                      </td>
                      <td style={styles.tdSummary}>{buildSummary(row)}</td>
                      <td style={styles.tdAction}>
                        <button
                          style={styles.detailBtn}
                          onClick={() => setSelectedRow(row)}
                        >
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedRow && (
        <div style={styles.modalOverlay} onClick={() => setSelectedRow(null)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
 
               <h2 style={styles.modalTitle}>
                  {EVENT_LABELS[selectedRow.event_type] || selectedRow.event_type}
                </h2>
                <p style={styles.modalSubtitle}>
                  {selectedRow.username} · {formatDateTime(selectedRow.performed_at)}
                </p>
              </div>

              <button style={styles.secondaryBtn} onClick={() => setSelectedRow(null)}>
                Cerrar
              </button>
            </div>

            <div style={styles.detailMeta}>
            <div><strong>Resumen:</strong> {buildSummary(selectedRow)}</div>
            {getReason(selectedRow) ? (
            <div><strong>Motivo del cambio:</strong> {getReason(selectedRow)}</div>
            ) : null}
            <div><strong>Entidad:</strong> {selectedRow.entity_type || "—"}</div>
            <div><strong>ID afectado:</strong> {selectedRow.entity_id || "—"}</div>
            <div><strong>ID evento:</strong> {selectedRow.id}</div>
            </div>
            <div style={styles.detailGrid}>
              <div style={styles.jsonCard}>
                <div style={styles.jsonTitle}>Valores anteriores</div>
                <pre style={styles.pre}>{prettyJson(selectedRow.old_values)}</pre>
              </div>

              <div style={styles.jsonCard}>
                <div style={styles.jsonTitle}>Valores nuevos</div>
                <pre style={styles.pre}>{prettyJson(selectedRow.new_values)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#e9f6fb",
    padding: "32px 16px",
  },
  container: {
    maxWidth: 1320,
    margin: "0 auto",
    display: "grid",
    gap: 18,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  mainTitle: {
    margin: 0,
    color: "#0284c7",
    fontSize: "clamp(28px, 3vw, 42px)",
    fontWeight: 900,
  },
  subtitle: {
    margin: "6px 0 0 0",
    color: "#475569",
    fontSize: 16,
    fontWeight: 700,
  },
  card: {
    background: "#fff",
    border: "1px solid #dbeafe",
    borderRadius: 18,
    padding: 20,
    boxShadow: "0 8px 20px rgba(2,6,23,.06)",
  },
  filtersRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchInput: {
    flex: "1 1 420px",
    minWidth: 260,
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    padding: "12px 14px",
    background: "#fff",
    fontWeight: 700,
    fontSize: 15,
    color: "#0f172a",
  },
  select: {
    minWidth: 240,
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    padding: "12px 14px",
    background: "#fff",
    fontWeight: 700,
    fontSize: 15,
    color: "#0f172a",
  },
  counterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 14px",
    borderRadius: 999,
    background: "#ffffff",
    border: "1px solid #dbeafe",
    color: "#0369a1",
    fontWeight: 800,
  },
  tableWrap: {
    background: "#fff",
    border: "1px solid #dbeafe",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(2,6,23,.06)",
    overflow: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 980,
  },
  th: {
    textAlign: "left",
    padding: "16px 20px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    color: "#334155",
    fontSize: 13,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "18px 20px",
    borderBottom: "1px solid #eef2f7",
    color: "#0f172a",
    fontWeight: 700,
    verticalAlign: "middle",
  },
  tdDate: {
    padding: "18px 20px",
    borderBottom: "1px solid #eef2f7",
    color: "#0f172a",
    fontWeight: 800,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    minWidth: 210,
  },
  tdUser: {
    padding: "18px 20px",
    borderBottom: "1px solid #eef2f7",
    color: "#0f172a",
    fontWeight: 800,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    minWidth: 120,
  },
  tdSummary: {
    padding: "18px 20px",
    borderBottom: "1px solid #eef2f7",
    color: "#334155",
    fontWeight: 700,
    verticalAlign: "middle",
    minWidth: 320,
    lineHeight: 1.45,
  },
  tdAction: {
    padding: "18px 20px",
    borderBottom: "1px solid #eef2f7",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    textAlign: "center",
    minWidth: 150,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  detailBtn: {
    border: "1px solid #bfdbfe",
    borderRadius: 12,
    padding: "10px 14px",
    background: "#f8fbff",
    color: "#1d4ed8",
    fontWeight: 800,
    cursor: "pointer",
    minWidth: 120,
  },
  primaryBtn: {
    border: "none",
    borderRadius: 12,
    padding: "11px 18px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryBtn: {
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    padding: "11px 16px",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  },
  message: {
    borderRadius: 12,
    padding: "12px 14px",
    fontWeight: 800,
  },
  messageInfo: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
  },
  messageError: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
  },
  text: {
    margin: 0,
    color: "#334155",
    fontWeight: 700,
  },
  title: {
    margin: "0 0 10px 0",
    color: "#0f172a",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 1000,
  },
  modalCard: {
    width: "min(1000px, 96vw)",
    maxHeight: "90vh",
    overflow: "auto",
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #dbeafe",
    boxShadow: "0 24px 60px rgba(2,6,23,.22)",
    display: "grid",
    gap: 16,
    padding: 22,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },
  modalTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 24,
    fontWeight: 900,
  },
  modalSubtitle: {
    margin: "6px 0 0 0",
    color: "#64748b",
    fontWeight: 700,
  },
  detailMeta: {
    display: "grid",
    gap: 8,
    color: "#334155",
    fontWeight: 700,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 14,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 14,
  },
  jsonCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    background: "#f8fafc",
    overflow: "hidden",
  },
  jsonTitle: {
    padding: "12px 14px",
    borderBottom: "1px solid #e2e8f0",
    fontWeight: 900,
    color: "#334155",
    background: "#fff",
  },
  pre: {
    margin: 0,
    padding: 14,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 13,
    lineHeight: 1.5,
    color: "#0f172a",
    fontFamily: "Consolas, Monaco, monospace",
  },
auditNote: {
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  borderRadius: 12,
  padding: "12px 14px",
  fontWeight: 700,
  fontSize: 14,
},
};
